import { maskCpfCnpj } from "../../../utils/mask";
import { unmaskMoney } from "../../../utils/mask";
import {
  IApiRequestPagamento,
  IApiRequestProposta,
  IApiResponseProposta,
  IImovelEndereco,
  IProponente,
  IProposta,
  IRequestProponentes,
} from "../types/proposta";
import { isCompleteDocument, normalizeCpfDigits } from "./proponenteIdentity";
import { parseAxis } from "./propostaConstants";

type EnderecoApiInput =
  | {
      cep?: string;
      logradouro?: string;
      numero?: string | number;
      bairro?: string;
      cidade?: string;
      estado?: string;
      uf?: string;
    }
  | null
  | undefined;

export function moneyToNumber(
  value: string | number | null | undefined,
): number {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number") {
    if (!isFinite(value) || isNaN(value)) return 0;
    return value;
  }
  return unmaskMoney(value);
}

function resolveProponentePhone(
  telefone: string,
  codPais?: string,
): { nationalDigits: string; codPais: string } {
  const digits = (telefone || "").replace(/\D/g, "");
  return { nationalDigits: digits, codPais: codPais || "+55" };
}

function mapEnderecoFromApi(
  endereco?: EnderecoApiInput,
): IImovelEndereco | undefined {
  if (!endereco) return undefined;

  const estado = endereco.estado ?? "";
  const mapped: IImovelEndereco = {
    cep: endereco.cep ?? "",
    logradouro: endereco.logradouro ?? "",
    numero: endereco.numero != null ? String(endereco.numero) : "",
    bairro: endereco.bairro ?? "",
    cidade: endereco.cidade ?? "",
    estado,
    uf: endereco.uf ?? estado,
  };

  const hasData = Object.values(mapped).some((value) => Boolean(value));
  return hasData ? mapped : undefined;
}

export function propostaApiToLocal(
  propostaApi: IApiResponseProposta,
): IProposta {
  const propostaLocal = {} as IProposta;
  const proponentes: IProponente[] = [];

  propostaLocal.id_proposta = propostaApi.proposta[0].id;
  propostaLocal.id_corretor = propostaApi.proposta[0].id_corretor;
  propostaLocal.id_empresa = propostaApi.proposta[0].id_empresa;
  propostaLocal.comissao = Number(propostaApi.proposta[0].comissao) || 4;
  propostaLocal.is_ppm = null;
  propostaLocal.tipo_proposta = `${propostaApi.proposta[0].tipo_proposta}`;
  propostaLocal.axis = parseAxis(propostaApi.proposta[0].axis);
  propostaLocal.id_origem_lead = `${propostaApi.proposta[0].origem_lead}`;
  propostaLocal.observacao = `${propostaApi.proposta[0].observacao}`;
  propostaLocal.uuid = `${propostaApi.proposta[0].uuid}`;
  propostaLocal.status = propostaApi.proposta[0].status;
  propostaLocal.status_kanban = propostaApi.proposta[0].status_kanban;
  propostaLocal.corretores = propostaApi.corretores || [];
  propostaLocal.published = propostaApi.proposta[0].status !== -1;

  propostaApi.cliente.forEach((c) => {
    const phone = resolveProponentePhone(c.telefone || "", c.cod_pais);
    proponentes.push({
      id_pessoa: c.pessoaid,
      id_cliente: c.id_cliente,
      nome: c.nome,
      cpfCnpj: maskCpfCnpj(c.cpfcnpj),
      rg: c.rg,
      emissorRG: c.emissor_rg ? c.emissor_rg : "",
      dataNascimento: formatDate(new Date(c.datanascimento)),
      naturalidade: c.naturalidade,
      nacionalidade: c.nacionalidade,
      estadoCivil: c.estadocivil,
      telefone: phone.nationalDigits,
      cod_pais: phone.codPais,
      email: c.email,
      profissao: c.profissao,
      rendaMensal: Number(c.renda_mensal).toLocaleString("pt-br", {
        style: "currency",
        currency: "BRL",
      }),
      observacoes: c.observacao,
      cep: c.cep,
      logradouro: c.logradouro,
      numero: c.numero ? `${c.numero}` : "",
      complemento: c.complemento || "",
      bairro: c.bairro,
      estado: c.estado,
      cidade: c.cidade,
      codibge: c.codibge,
    });
  });

  propostaApi.proponentes_secundarios.forEach((c) => {
    const phone = resolveProponentePhone(c.telefone || "", c.cod_pais);
    proponentes.push({
      id_pessoa: c.pessoaid,
      id_cliente: c.cliente_id,
      nome: c.nome,
      cpfCnpj: maskCpfCnpj(c.cpfcnpj),
      rg: c.rg,
      emissorRG: c.emissor_rg ? c.emissor_rg : "",
      dataNascimento: formatDate(new Date(c.datanascimento)),
      naturalidade: c.naturalidade,
      nacionalidade: c.nacionalidade,
      estadoCivil: c.estadocivil,
      telefone: phone.nationalDigits,
      cod_pais: phone.codPais,
      email: c.email,
      profissao: c.profissao,
      rendaMensal: Number(c.rendaMensal).toLocaleString("pt-br", {
        style: "currency",
        currency: "BRL",
      }),
      observacoes: c.observacao,
      cep: c.cep,
      logradouro: c.logradouro,
      numero: c.numero || "",
      complemento: c.complemento || "",
      bairro: c.bairro,
      estado: c.estado,
      cidade: c.cidade,
      codibge: c.codibge,
    });
  });

  propostaLocal.proponentes = proponentes;

  const propostaImovelApi = propostaApi.proposta[0];
  const idImovelLocal = propostaImovelApi?.id_imovel_kurole
    ? `${propostaImovelApi.id_imovel_kurole}`
    : "";

  propostaLocal.imovel = {
    id_imovel: idImovelLocal,
    id_imovel_kurole: propostaImovelApi?.id_imovel_kurole
      ? `${propostaImovelApi.id_imovel_kurole}`
      : undefined,
    descricao_imovel: propostaImovelApi?.descricao_imovel || "",
    bloco: propostaImovelApi?.bloco || "",
    unidade: propostaImovelApi?.unidade || "",
    imovel_seminovo: propostaImovelApi?.imovel_seminovo || 0,
    id_construtora: `${propostaImovelApi?.id_construtora}` || "",
    descricao_construtora: "",
    valor: propostaImovelApi?.valor
      ? Number(propostaImovelApi?.valor).toLocaleString("pt-br", {
          style: "currency",
          currency: "BRL",
        })
      : "0,00",
    endereco:
      Number(idImovelLocal) === -1
        ? mapEnderecoFromApi(propostaImovelApi?.endereco)
        : undefined,
  };

  propostaLocal.pagamentos = propostaApi?.pagamentos.map((p) => ({
    valor: Number(p?.valor).toLocaleString("pt-br", {
      style: "currency",
      currency: "BRL",
    }),
    id_formapagcondpag: `${p?.id_formapagcondpag}`,
    forma: p?.nome,
    condicao: p?.descricao,
    qtd_parcelas: `${p?.qtd_parcelas}`,
    descricao: `${p?.nome} - ${p?.descricao}`,
    detalhamento: p?.detalhamento || "",
    dt_vencimento: p?.dt_vencimento
      ? (() => {
          const d = new Date(p.dt_vencimento);
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, "0");
          const dd = String(d.getDate()).padStart(2, "0");
          return `${yyyy}-${mm}-${dd}`;
        })()
      : "",
  }));

  return propostaLocal;
}

export function propostaLocalToApi(
  propostaLocal: IProposta,
): IApiRequestProposta {
  const propostaRequest = {} as IApiRequestProposta;
  propostaRequest.id_corretor = propostaLocal.id_corretor;
  propostaRequest.is_ppm = propostaLocal.is_ppm ? 1 : null;
  propostaRequest.id_empresa = propostaLocal.id_empresa;
  propostaRequest.comissao = propostaLocal.comissao;
  propostaRequest.id_proposta = propostaLocal.id_proposta
    ? `${propostaLocal.id_proposta}`
    : null;
  propostaRequest.origem_lead = Number(propostaLocal.id_origem_lead);
  propostaRequest.tipo_proposta = Number(propostaLocal.tipo_proposta) || 0;
  if (!propostaLocal.is_ppm) {
    propostaRequest.axis = parseAxis(propostaLocal.axis);
  }
  propostaRequest.observacao = propostaLocal.observacao;
  propostaRequest.published = propostaLocal.published;
  if (propostaLocal.corretores) {
    propostaRequest.corretores = propostaLocal.corretores.map((c) => c.id);
  }

  const isImovelNovo = Number(propostaLocal.imovel?.id_imovel) === -1;
  const endereco = propostaLocal.imovel?.endereco;

  propostaRequest.imovel = {
    ...(propostaLocal.imovel.id_imovel_kurole && !isImovelNovo
      ? { id_imovel_kurole: propostaLocal.imovel.id_imovel_kurole }
      : {}),
    descricao_imovel: propostaLocal.imovel.descricao_imovel,
    bloco: propostaLocal.imovel.bloco,
    unidade: propostaLocal.imovel.unidade,
    imovel_seminovo: propostaLocal.imovel.imovel_seminovo ? 1 : 0,
    id_construtora: Number(propostaLocal.imovel.id_construtora),
    valor: `${moneyToNumber(propostaLocal.imovel.valor)}`,
    ...(propostaLocal.is_ppm && isImovelNovo
      ? {
          endereco: {
            logradouro: endereco?.logradouro ?? "",
            cidade: endereco?.cidade ?? "",
            estado: endereco?.estado ?? "",
            cep: endereco?.cep ?? "",
            numero: endereco?.numero ?? "",
            bairro: endereco?.bairro ?? "",
            uf: endereco?.uf ?? endereco?.estado ?? "",
          },
        }
      : {}),
  };

  const requestPagamentos = propostaLocal.pagamentos.map(
    (p) =>
      ({
        id_formapagcondpag: Number(p.id_formapagcondpag),
        qtd_parcelas: Number(p.qtd_parcelas),
        dt_vencimento: p.dt_vencimento,
        detalhamento: p.detalhamento || "",
        valor: `${moneyToNumber(p.valor)}`,
      }) as IApiRequestPagamento,
  );

  propostaRequest.pagamentos = requestPagamentos;

  const proponentesRequest: IRequestProponentes[] = [];

  propostaLocal.proponentes.forEach((p) => {
    const phoneDigits = (p.telefone || "").replace(/\D/g, "");
    const cpfDigits = normalizeCpfDigits(p.cpfCnpj);
    const canSendIdCliente =
      typeof p.id_cliente === "number" &&
      p.id_cliente > 0 &&
      isCompleteDocument(cpfDigits);

    proponentesRequest.push({
      ...(canSendIdCliente ? { id_cliente: p.id_cliente } : {}),
      nome: p.nome,
      email: p.email,
      telefone: phoneDigits,
      cod_pais: p.cod_pais || "+55",
      profissao: p.profissao,
      origem_lead: Number(propostaLocal.id_origem_lead),
      estadocivil: `${p.estadoCivil}`,
      rg: p.rg,
      emissor_rg: p.emissorRG || "",
      cpfcnpj: maskCpfCnpj(p.cpfCnpj),
      datanascimento: convertDateBrasilToAmerican(p.dataNascimento),
      observacao: p.observacoes || "",
      nacionalidade: p.nacionalidade,
      naturalidade: p.naturalidade,
      renda_mensal: `${moneyToNumber(p.rendaMensal)}`,
      cep: p.cep,
      logradouro: p.logradouro,
      cidade: p.cidade,
      estado: p.estado,
      bairro: p.bairro,
      pais: "BRASIL",
      codibge: p.codibge,
      numero: p.numero ? p.numero : "",
      complemento: p.complemento ? p.complemento : "",
    });
  });

  propostaRequest.proponentes = proponentesRequest;

  return propostaRequest;
}

export function formatDate(date: Date): string {
  const day = String(date.getUTCDate()).padStart(2, "0");
  const month = String(date.getUTCMonth() + 1).padStart(2, "0");
  const year = date.getUTCFullYear();
  return `${day}/${month}/${year}`;
}

function convertDateBrasilToAmerican(dateString: string): string {
  if (!dateString) return "";
  if (dateString.includes("-")) return dateString;
  const [day, month, year] = dateString.split("/");
  return `${year}-${month}-${day}`;
}

export function camposInvalidos(
  proposta: IProposta,
  lockImovelEdit: boolean,
  published = false,
): string[] {
  const invalidsInputs: string[] = [];

  if (!proposta.id_origem_lead || !Number(proposta.id_origem_lead)) {
    invalidsInputs.push(' O campo "origem do lead" é obrigatório');
  }

  if (!proposta.tipo_proposta || !Number(proposta.tipo_proposta)) {
    invalidsInputs.push(' O campo "tipo de proposta" é obrigatório');
  }

  if (published && proposta.axis !== 0 && proposta.axis !== 1) {
    invalidsInputs.push(
      ' O campo "AXIS (Sim/Não)" é obrigatório para o envio da proposta',
    );
  }

  if (!proposta.imovel?.descricao_imovel) {
    invalidsInputs.push(' O campo "descrição do imóvel" é obrigatório');
  }
  if (!lockImovelEdit && !proposta.imovel?.bloco) {
    invalidsInputs.push(' O campo "bloco" é obrigatório');
  }
  if (!lockImovelEdit && !proposta.imovel?.unidade) {
    invalidsInputs.push(' O campo "unidade" é obrigatório');
  }
  if (!lockImovelEdit && !proposta.imovel?.id_construtora) {
    invalidsInputs.push(' O campo "construtora" é obrigatório');
  }
  if (!proposta.imovel?.valor) {
    invalidsInputs.push(' O campo "valor do imóvel" é obrigatório');
  }

  if (proposta.proponentes?.length) {
    proposta.proponentes.forEach((p, index) => {
      if (!p.nome)
        invalidsInputs.push(
          ` O campo nome do proponente ${index + 1} é obrigatório`,
        );
      if (!p.cpfCnpj)
        invalidsInputs.push(
          ` O campo cpf do proponente ${index + 1} é obrigatório`,
        );
      if (!validarCpfCnpj(p.cpfCnpj)) {
        invalidsInputs.push(` CPF ou CNPJ do proponente ${index + 1} inválido`);
      }
      if ((p.telefone || "").replace(/\D/g, "").length < 8) {
        invalidsInputs.push(
          ` O campo telefone do proponente ${index + 1} é obrigatório`,
        );
      }
      if (!p.email)
        invalidsInputs.push(
          ` O campo email do proponente ${index + 1} é obrigatório`,
        );
      if (!p.estadoCivil)
        invalidsInputs.push(
          ` O campo estado civil do proponente ${index + 1} é obrigatório`,
        );
      if (!p.dataNascimento)
        invalidsInputs.push(
          ` O campo data de nascimento do proponente ${index + 1} é obrigatório`,
        );
      if (!p.naturalidade)
        invalidsInputs.push(
          ` O campo naturalidade do proponente ${index + 1} é obrigatório`,
        );
      if (!p.nacionalidade)
        invalidsInputs.push(
          ` O campo nacionalidade do proponente ${index + 1} é obrigatório`,
        );
      if (!p.rendaMensal)
        invalidsInputs.push(
          ` O campo renda mensal do proponente ${index + 1} é obrigatório`,
        );
      if (!p.cep)
        invalidsInputs.push(
          ` O campo cep do proponente ${index + 1} é obrigatório`,
        );
      if (!p.logradouro)
        invalidsInputs.push(
          ` O campo logradouro do proponente ${index + 1} é obrigatório`,
        );
      if (!p.bairro)
        invalidsInputs.push(
          ` O campo bairro do proponente ${index + 1} é obrigatório`,
        );
      if (!p.cidade)
        invalidsInputs.push(
          ` O campo cidade do proponente ${index + 1} é obrigatório`,
        );
      if (!p.estado)
        invalidsInputs.push(
          ` O campo estado do proponente ${index + 1} é obrigatório`,
        );
    });
  } else {
    invalidsInputs.push(' O campo "proponente" é obrigatório');
  }

  let valorPago = 0;
  if (proposta.pagamentos?.length) {
    proposta.pagamentos.forEach((p, index) => {
      valorPago += moneyToNumber(p.valor);
      if (!p.id_formapagcondpag)
        invalidsInputs.push(
          ` O campo forma de pagamento ${index + 1} é obrigatório`,
        );
      if (!p.qtd_parcelas)
        invalidsInputs.push(
          ` O campo qtd de parcelas ${index + 1} é obrigatório`,
        );
      if (!p.valor)
        invalidsInputs.push(
          ` O campo valor do pagamento ${index + 1} é obrigatório`,
        );
    });
  } else {
    invalidsInputs.push(' O campo "pagamento" é obrigatório');
  }

  if (Number(valorPago.toFixed(2)) !== moneyToNumber(proposta.imovel?.valor)) {
    invalidsInputs.push(
      " O valor adicionado deve ser igual ao valor da proposta",
    );
  }

  return invalidsInputs;
}

export function validarCpfCnpj(valor: string): boolean {
  const digits = valor.replace(/\D/g, "");
  if (digits.length === 11) return validarCPF(digits);
  if (digits.length === 14) return validarCNPJ(digits);
  return false;
}

function validarCPF(cpf: string): boolean {
  if (cpf.length !== 11 || /^(\d)\1+$/.test(cpf)) return false;
  let soma = 0;
  for (let i = 0; i < 9; i++) soma += parseInt(cpf.charAt(i)) * (10 - i);
  let resto = soma % 11;
  const digito1 = resto < 2 ? 0 : 11 - resto;
  soma = 0;
  for (let i = 0; i < 10; i++) soma += parseInt(cpf.charAt(i)) * (11 - i);
  resto = soma % 11;
  const digito2 = resto < 2 ? 0 : 11 - resto;
  return (
    digito1 === parseInt(cpf.charAt(9)) && digito2 === parseInt(cpf.charAt(10))
  );
}

function validarCNPJ(cnpj: string): boolean {
  if (cnpj.length !== 14 || /^(\d)\1+$/.test(cnpj)) return false;
  let tamanho = cnpj.length - 2;
  let numeros = cnpj.substring(0, tamanho);
  const digitos = cnpj.substring(tamanho);
  let soma = 0;
  let pos = tamanho - 7;
  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  let resto = soma % 11;
  const digito1 = resto < 2 ? 0 : 11 - resto;
  tamanho = tamanho + 1;
  numeros = cnpj.substring(0, tamanho);
  soma = 0;
  pos = tamanho - 7;
  for (let i = tamanho; i >= 1; i--) {
    soma += parseInt(numeros.charAt(tamanho - i)) * pos--;
    if (pos < 2) pos = 9;
  }
  resto = soma % 11;
  const digito2 = resto < 2 ? 0 : 11 - resto;
  return (
    digito1 === parseInt(digitos.charAt(0)) &&
    digito2 === parseInt(digitos.charAt(1))
  );
}

export function getLockImovelEdit(proposta: IProposta): boolean {
  // HUB (igual App): proposta já salva trava cadastro do imóvel;
  // só o valor da proposta permanece editável em Pendentes.
  return Boolean(proposta?.id_proposta);
}
